import { isPushSupported, readPermissionStatus } from './permissionSync';
import * as serviceWorker from './serviceWorker';

/**
 * Thin adapter over the Firebase JS SDK (modular API). Loaded lazily with `import()` so a brand that
 * never enables web push does not download it, and so tests can swap it.
 *
 * The SDK is used for the token only: the push worker is this site's own
 * (`public/firebase-messaging-sw.js`), handed to `getToken` as `serviceWorkerRegistration`.
 *
 * Target kind: the registration token. The web SDK has no `onTokenRefresh`; it renews the token
 * itself inside `getToken`, and every sync reads it again, so a rotation reaches the engine on the
 * next sync (a new value changes the sync fingerprint). The value is never logged.
 */
const APP_NAME = 'notifications-v2';

let sdk = null;

const load = async () => {
  if (!sdk) {
    const [app, messaging] = await Promise.all([
      import('firebase/app'),
      import('firebase/messaging'),
    ]);
    sdk = { app, messaging };
  }
  return sdk;
};

/** Tests only. */
export const __setSdk = value => {
  sdk = value;
};

const webConfigOf = config => {
  const raw = config?.firebase?.web_config;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw || null;
};

/** The Firebase app of this brand; re-created if the brand moved to another Firebase app. */
const appFor = async (app, webConfig) => {
  const existing = app.getApps().find(a => a.name === APP_NAME);
  if (existing && existing.options?.appId === webConfig.appId) {
    return existing;
  }
  if (existing) {
    await app.deleteApp(existing);
  }
  return app.initializeApp(webConfig, APP_NAME);
};

/** Push on this browser: the APIs exist and Firebase Messaging supports it. */
export const isSupported = async () => {
  if (!isPushSupported()) {
    return false;
  }
  try {
    const { messaging } = await load();
    return await messaging.isSupported();
  } catch {
    return false;
  }
};

/**
 * The current target, or null when there can be none: no permission (`getToken` would prompt by
 * itself, outside of a user action) or no web config. Throws when the token cannot be read.
 */
export const getTarget = async config => {
  if ((await readPermissionStatus()) !== 'authorized') {
    return null;
  }

  const webConfig = webConfigOf(config);
  const vapidKey = config?.firebase?.vapid_public_key;
  if (!webConfig || !vapidKey) {
    return null;
  }

  const { app, messaging } = await load();
  if (!(await messaging.isSupported())) {
    return null;
  }

  const serviceWorkerRegistration = await serviceWorker.register(config);
  const instance = messaging.getMessaging(await appFor(app, webConfig));
  const value = await messaging.getToken(instance, {
    vapidKey,
    serviceWorkerRegistration,
  });

  return value ? { kind: 'registration_token', value } : null;
};

export const deleteTarget = async () => {
  try {
    const { app, messaging } = await load();
    const existing = app.getApps().find(a => a.name === APP_NAME);
    if (existing) {
      await messaging.deleteToken(messaging.getMessaging(existing));
    }
  } catch (err) {
    console.warn('[NotificationsV2] cannot delete target', err?.message);
  }
};
